import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import SourcesSidebar from '../components/SourcesSidebar';
import StudioPanel from '../components/StudioPanel';
import AnalysisTabs from '../components/AnalysisTabs';
import {
  analyzeDocument,
  analyzeMultipleDocuments,
  chatWithDocument,
  chatWithMultipleDocuments,
  deleteDocument,
  getDocument,
  getDocuments,
  suggestDocumentQuestions,
  suggestPortfolioQuestions,
  uploadDocument,
  uploadDocumentBatch,
} from '../services/api';

const NOTES_KEY = 'notebooklm_notes';

function loadNotes() {
  try { return JSON.parse(localStorage.getItem(NOTES_KEY) || '[]'); } catch { return []; }
}

function saveNotes(notes) {
  try { localStorage.setItem(NOTES_KEY, JSON.stringify(notes)); } catch { /* ignore */ }
}

function buildNotebookData(activeDocument, activeAnalysis, portfolioAnalysis) {
  if (portfolioAnalysis) {
    return {
      title: `Portfolio (${portfolioAnalysis._docCount || '?'} sources)`,
      summary: portfolioAnalysis.summary,
      financialHealthAssessment: portfolioAnalysis.financialHealthAssessment || '',
      keyInsights: portfolioAnalysis.keyInsights || [],
      riskSignals: portfolioAnalysis.riskSignals || [],
      nextActions: portfolioAnalysis.nextActions || [],
      crossDocumentFindings: portfolioAnalysis.crossDocumentFindings || [],
      incomeCategories: portfolioAnalysis.incomeCategories || [],
      liabilitySignals: portfolioAnalysis.liabilitySignals || [],
      transactionHighlights: portfolioAnalysis.transactionHighlights || [],
      transactionTable: portfolioAnalysis.transactionTable || [],
      mindMapMermaid: portfolioAnalysis.mindMapMermaid || '',
      entities: {},
    };
  }

  if (!activeDocument) return null;

  const extracted = activeAnalysis?.extractedData || {};
  return {
    title: activeDocument.originalName || 'Untitled',
    summary: activeAnalysis?.summary,
    financialHealthAssessment: '',
    keyInsights: activeAnalysis?.keyInsights || [],
    riskSignals: extracted.riskSignals || [],
    nextActions: extracted.nextActions || [],
    crossDocumentFindings: extracted.transactionThemes || [],
    incomeCategories: [],
    liabilitySignals: [],
    transactionHighlights: [],
    transactionTable: [],
    mindMapMermaid: '',
    entities: extracted.entities || {},
  };
}

export default function Dashboard() {
  const { user, handleLogout } = useContext(AuthContext);

  // Sources
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  // Active document / portfolio
  const [activeDocument, setActiveDocument] = useState(null);
  const [portfolioAnalysis, setPortfolioAnalysis] = useState(null);

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);

  // Upload
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Analysis
  const [analyzingId, setAnalyzingId] = useState('');
  const [portfolioLoading, setPortfolioLoading] = useState(false);

  // Chat
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatScope, setChatScope] = useState('single');

  // Suggested questions
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);

  // Notes (localStorage)
  const [notes, setNotes] = useState(loadNotes);

  // Status
  const [statusMsg, setStatusMsg] = useState('');
  const [statusType, setStatusType] = useState('info');
  const [errorMsg, setErrorMsg] = useState('');

  const activeAnalysis = useMemo(() => activeDocument?.analysis || {}, [activeDocument]);

  const notebookData = useMemo(
    () => buildNotebookData(activeDocument, activeAnalysis, portfolioAnalysis),
    [activeDocument, activeAnalysis, portfolioAnalysis]
  );

  const portfolioMode = Boolean(portfolioAnalysis);

  const showStatus = useCallback((msg, type = 'info') => {
    setStatusMsg(msg);
    setStatusType(type);
    setErrorMsg('');
  }, []);

  const showError = useCallback((msg) => {
    setErrorMsg(msg);
    setStatusMsg('');
  }, []);

  // ── Fetch documents ──
  const fetchDocuments = useCallback(async ({ openFirst = true } = {}) => {
    setLoadingDocs(true);
    try {
      const res = await getDocuments();
      const docs = res.data.documents || [];
      setDocuments(docs);
      if (openFirst && !activeDocument && !portfolioAnalysis && docs.length) {
        await openDoc(docs[0]._id, docs);
      }
    } catch {
      showError('Failed to load sources.');
    } finally {
      setLoadingDocs(false);
    }
  }, []); // intentionally empty — fetchDocuments is stable

  useEffect(() => {
    if (user) fetchDocuments();
  }, [user]); // fetchDocuments is memoized above

  // ── Open a document ──
  const openDoc = async (id, docList) => {
    try {
      const res = await getDocument(id);
      setActiveDocument(res.data.document);
      setPortfolioAnalysis(null);
      setChatScope('single');
      setChatMessages([]);
      setSuggestedQuestions([]);
      setErrorMsg('');

      const doc = res.data.document;
      if (doc.analysis?.status === 'completed' && doc.analysis?.summary) {
        loadSuggestedQuestions(id);
      }
    } catch {
      showError('Could not open source.');
    }
  };

  // ── Suggested questions ──
  const loadSuggestedQuestions = async (id) => {
    setSuggestLoading(true);
    try {
      const res = await suggestDocumentQuestions(id);
      setSuggestedQuestions(res.data.questions || []);
    } catch {
      setSuggestedQuestions([]);
    } finally {
      setSuggestLoading(false);
    }
  };

  const loadPortfolioSuggestedQuestions = async (ids) => {
    setSuggestLoading(true);
    try {
      const res = await suggestPortfolioQuestions({ documentIds: ids });
      setSuggestedQuestions(res.data.questions || []);
    } catch {
      setSuggestedQuestions([]);
    } finally {
      setSuggestLoading(false);
    }
  };

  // ── Upload single file ──
  const handleUploadFile = async (files) => {
    const fileList = Array.isArray(files) ? files : [files];
    setUploading(true);
    setUploadProgress(0);
    const onUploadProgress = (evt) => {
      if (evt.total) setUploadProgress(Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
    };
    try {
      if (fileList.length === 1) {
        showStatus('Uploading…');
        const formData = new FormData();
        formData.append('document', fileList[0]);
        const res = await uploadDocument(formData, { onUploadProgress });
        showStatus('Upload complete. Click Analyze to extract insights.', 'success');
        await fetchDocuments({ openFirst: false });
        await openDoc(res.data.document._id);
      } else {
        showStatus(`Uploading ${fileList.length} files…`);
        const formData = new FormData();
        fileList.forEach((f) => formData.append('documents', f));
        const res = await uploadDocumentBatch(formData, { onUploadProgress });
        const uploadedIds = (res.data.documents || []).map((d) => d._id);
        setSelectedIds((prev) => Array.from(new Set([...prev, ...uploadedIds])));
        showStatus(`${uploadedIds.length} files uploaded and selected for portfolio analysis.`, 'success');
        await fetchDocuments({ openFirst: false });
      }
    } catch (err) {
      showError(err.response?.data?.message || 'Upload failed.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ── Upload folder ──
  const handleUploadFolder = async (files) => {
    setUploading(true);
    setUploadProgress(0);
    showStatus(`Uploading ${files.length} files…`);
    const formData = new FormData();
    files.forEach((f) => formData.append('documents', f));
    try {
      const res = await uploadDocumentBatch(formData, {
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
        },
      });
      const uploadedIds = (res.data.documents || []).map((d) => d._id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...uploadedIds])));
      showStatus(`${uploadedIds.length} files uploaded and selected for portfolio analysis.`, 'success');
      await fetchDocuments({ openFirst: false });
    } catch (err) {
      showError(err.response?.data?.message || 'Folder upload failed.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // ── Analyze single document ──
  const handleAnalyze = async (id) => {
    setAnalyzingId(id);
    showStatus('Running AI analysis…');
    try {
      const res = await analyzeDocument(id);
      if (activeDocument?._id === id) {
        setActiveDocument((prev) => ({ ...prev, analysis: res.data.analysis }));
      }
      setPortfolioAnalysis(null);
      showStatus('Analysis complete!', 'success');
      await fetchDocuments({ openFirst: false });
      await openDoc(id);
      loadSuggestedQuestions(id);
    } catch (err) {
      showError(err.response?.data?.message || 'Analysis failed.');
    } finally {
      setAnalyzingId('');
    }
  };

  // ── Delete document ──
  const handleDelete = async (id) => {
    try {
      await deleteDocument(id);
      if (activeDocument?._id === id) {
        setActiveDocument(null);
        setChatMessages([]);
        setSuggestedQuestions([]);
      }
      setSelectedIds((prev) => prev.filter((x) => x !== id));
      await fetchDocuments({ openFirst: false });
    } catch (err) {
      showError(err.response?.data?.message || 'Delete failed.');
    }
  };

  // ── Portfolio analysis ──
  const handlePortfolioAnalyze = async () => {
    if (!selectedIds.length) {
      showError('Select at least one source for portfolio analysis.');
      return;
    }
    setPortfolioLoading(true);
    setSuggestedQuestions([]);
    showStatus(`Analyzing ${selectedIds.length} sources…`);
    try {
      const res = await analyzeMultipleDocuments({ documentIds: selectedIds });
      const portfolio = { ...res.data.portfolio, _docCount: selectedIds.length };
      setPortfolioAnalysis(portfolio);
      setActiveDocument(null);
      setChatScope('portfolio');
      setChatMessages([]);
      const stats = res.data?.stats || {};
      showStatus(
        `Portfolio ready — ${stats.analyzedCount || 0} analyzed, ${stats.reusedCount || 0} reused.`,
        'success'
      );
      await loadPortfolioSuggestedQuestions(selectedIds);
      await fetchDocuments({ openFirst: false });
    } catch (err) {
      showError(err.response?.data?.message || 'Portfolio analysis failed.');
    } finally {
      setPortfolioLoading(false);
    }
  };

  // ── Chat ──
  const handleAsk = async (question) => {
    if (chatScope === 'single' && !activeDocument?._id) {
      showError('Open a source to chat in single mode.');
      return;
    }
    if (chatScope === 'portfolio' && !selectedIds.length) {
      showError('Select sources for portfolio chat mode.');
      return;
    }

    setChatLoading(true);
    setChatMessages((prev) => [...prev, { role: 'user', content: question }]);

    try {
      const res = chatScope === 'portfolio'
        ? await chatWithMultipleDocuments({ documentIds: selectedIds, question })
        : await chatWithDocument(activeDocument._id, { question });
      setChatMessages((prev) => [...prev, { role: 'assistant', content: res.data.answer }]);
    } catch (err) {
      const msg = err.response?.data?.message || 'Unable to respond right now.';
      setChatMessages((prev) => [...prev, { role: 'assistant', content: msg }]);
    } finally {
      setChatLoading(false);
    }
  };

  // ── Notes ──
  const addNote = (text) => {
    const note = { id: Date.now(), text, createdAt: new Date().toISOString() };
    const updated = [note, ...notes];
    setNotes(updated);
    saveNotes(updated);
  };

  const deleteNote = (id) => {
    const updated = notes.filter((n) => n.id !== id);
    setNotes(updated);
    saveNotes(updated);
  };

  // ── Selection ──
  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const selectAll = () => setSelectedIds(documents.map((d) => d._id));
  const clearAll  = () => setSelectedIds([]);

  // ── Scope change ──
  const handleScopeChange = (scope) => {
    setChatScope(scope);
    setChatMessages([]);
  };

  if (!user) return null;

  const notebookTitle = portfolioAnalysis
    ? `Portfolio — ${selectedIds.length} sources`
    : activeDocument?.originalName || null;

  const notebookUpdated = portfolioAnalysis
    ? 'Just now'
    : activeAnalysis?.processedAt
      ? new Date(activeAnalysis.processedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      : null;

  return (
    <div className="nb-shell">
      {/* ── HEADER ── */}
      <header className="nb-header">
        <div className="nb-logo">
          <div className="nb-logo-icon">📚</div>
          <div>
            <div className="nb-logo-name">NotebookLLM</div>
            <div className="nb-logo-sub">Document Intelligence</div>
          </div>
        </div>

        <div className="nb-header-center" />

        <div className="nb-header-actions">
          <div className="nb-user-pill">
            <div className="nb-avatar">{user.name?.[0]?.toUpperCase() || 'U'}</div>
            {user.name}
          </div>
          <button className="btn-logout" onClick={handleLogout}>Sign out</button>
        </div>
      </header>

      {/* ── THREE-PANEL BODY ── */}
      <div className="nb-body">

        {/* LEFT — Sources */}
        <SourcesSidebar
          documents={documents}
          activeId={activeDocument?._id}
          selectedIds={selectedIds}
          loadingDocs={loadingDocs}
          uploading={uploading}
          uploadProgress={uploadProgress}
          portfolioLoading={portfolioLoading}
          analyzingId={analyzingId}
          onOpen={(id) => openDoc(id)}
          onToggleSelect={toggleSelect}
          onSelectAll={selectAll}
          onClearAll={clearAll}
          onAnalyze={handleAnalyze}
          onDelete={handleDelete}
          onUploadFile={handleUploadFile}
          onUploadFolder={handleUploadFolder}
          onPortfolioAnalyze={handlePortfolioAnalyze}
        />

        {/* CENTER — Analysis */}
        <main className="nb-main">
          {/* Status bars */}
          {statusMsg && (
            <div className={`status-bar ${statusType}`}>
              {statusType === 'info' && <span className="spinner spinner-sm" />}
              {statusMsg}
            </div>
          )}
          {errorMsg && (
            <div className="status-bar error">⚠️ {errorMsg}</div>
          )}

          {!notebookData && (
            <div className="nb-empty">
              <div className="nb-empty-icon">📓</div>
              <div className="nb-empty-title">No source open</div>
              <div className="nb-empty-text">
                Upload a PDF or image from the Sources panel, then click <strong>Analyze</strong> to extract insights and enable chat.
              </div>
            </div>
          )}

          {notebookData && (
            <>
              <div className="nb-title-bar">
                <div className="nb-title" title={notebookTitle || ''}>
                  {portfolioMode ? '🗂️ ' : '📄 '}{notebookTitle || 'Untitled'}
                </div>
                <div className="nb-title-meta">
                  {portfolioMode && (
                    <span>📁 {selectedIds.length} sources selected</span>
                  )}
                  {notebookUpdated && (
                    <span>🕐 {notebookUpdated}</span>
                  )}
                  {!portfolioMode && activeAnalysis?.extractedData?.documentType && (
                    <span>🏷️ {activeAnalysis.extractedData.documentType.replace(/_/g, ' ')}</span>
                  )}
                </div>
              </div>

              <AnalysisTabs
                notebookData={notebookData}
                portfolioMode={portfolioMode}
              />
            </>
          )}
        </main>

        {/* RIGHT — Studio */}
        <StudioPanel
          chatMessages={chatMessages}
          chatLoading={chatLoading}
          chatScope={chatScope}
          activeDocument={activeDocument}
          selectedIds={selectedIds}
          suggestedQuestions={suggestedQuestions}
          suggestLoading={suggestLoading}
          notes={notes}
          onScopeChange={handleScopeChange}
          onAsk={handleAsk}
          onAddNote={addNote}
          onDeleteNote={deleteNote}
        />
      </div>
    </div>
  );
}
